import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { FormLayoutElement } from "@/config/forms/general";
import { tiragesColumnsVisibility } from "@/config/dashboard/tirages";
import { Bibliography, Tirage, TirageInBibliography } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  TirageLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import TirageInBibliographyDrawer from "./drawer/TirageInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  tirage: Tirage | undefined;
  bibliography: Bibliography | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<TirageInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={
            row.original.bibliography?.attachments ??
            row.original.attachments ??
            []
          }
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionTitre",
    header: "bibliographieDescriptionTitre",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAuteur",
    header: "bibliographieDescriptionAuteur",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
];

const TirageInBibliographyLinks = ({
                                     id,
                                     tirage,
                                     fields,
                                     type,
                                     bibliography,
                                   }: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "tirageInBibliography",
    type,
  );

  const { mutate: deleteTirageInBibliography } = useDeleteComplexLink(
    id,
    "tirageInBibliography",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteTirageInBibliography({
        id,
        relation: "tirageInBibliography",
      });
    },
    [deleteTirageInBibliography],
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const renderTirageInBibliographyRowActions = useCallback(
    (data: TirageInBibliography) => {
          const preview =
            type === "bibliography"
              ? {
                  previewType: "tirage" as const,
                  previewId: data.tirage.id,
                  previewElements: [
                    {
                      label: t("tirageDescriptionNumeroEnregistrement"),
                      value: data.tirage.tirageDescriptionNumeroEnregistrement ?? "",
                      type: "text" as const,
                    },
                    {
                      label: t("tirageDescriptionNotes"),
                      value: data.tirage.tirageDescriptionNotes ?? "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.tirage.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "tirage"
                ? {
                    previewType: "bibliography" as const,
                    previewId: data.bibliography.id,
                    previewElements: [
                      {
                        label: t("bibliographieDescriptionNumeroBibliographie"),
                        value:
                          data.bibliography.bibliographieDescriptionNumeroBibliographie,
                        type: "text" as const,
                      },
                      {
                        label: t("bibliographieDescriptionResumeLong"),
                        value: data.bibliography.bibliographieDescriptionResumeLong,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.bibliography.attachments ?? [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<TirageInBibliography, any>[] = useMemo(() => {
    return [
      ...(type === "tirage"
        ? columnsForBibliography.map((column) => ({
          ...column,
          header: t(column.header),
        }))
        : []),
      ...(type === "bibliography"
        ? [
            {
              accessorKey: "tirage.tirageDescriptionNumeroEnregistrement",
              header: t("tirageDescriptionNumeroEnregistrement"),
              size: 120,
              cell: ({ row }: { row: Row<TirageInBibliography> }) => (
                <TextCell
                  value={
                    row.original.tirage?.tirageDescriptionNumeroEnregistrement
                    ?? ""
                  }
                />
              ),
            },
            {
              accessorKey: "tirageDescriptionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<TirageInBibliography> }) => (
                <ImageCell
                  value=""
                  attachments={
                    row.original.tirage?.attachments ??
                    row.original.attachments ??
                    []
                  }
                  domain="tirage"
                />
              ),
            },
          ]
        : []),
      {
        accessorKey: "tirageDansBibliographieDescriptionReproduitPage",
        header: t("tirageDansBibliographieDescriptionReproduitPage"),
        size: 50,
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionCitePage",
        header: t("tirageDansBibliographieDescriptionCitePage"),
        size: 50,
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionReproductionCouleur",
        header: t("tirageDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "tirageDansBibliographieDescriptionNonPagine",
        header: t("tirageDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "tirageDansBibliographieNotes",
        header: t("tirageDansBibliographieNotes"),
        cell: (props: any) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      createActionsColumnDef<TirageInBibliography>(({ row }) =>
        renderTirageInBibliographyRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "tirage") {
      return (
        <TirageToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          tirage={tirage}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("bibliographiesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    } else {
      return (
        <BibliographyToTirageLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderTirageInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("tiragesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
  }, [
    tirage,
    bibliography,
    columns,
    fields,
    id,
    isAddMode,
    relatedData,
    searchContent,
    type,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type TirageToBibliographyLinksProps = LinksTableProps<TirageInBibliography> & {
  tirage: Tirage | undefined;
};

type BibliographyToTirageLinksProps = LinksTableProps<TirageInBibliography> & {
  bibliography: Bibliography | undefined;
};

const TirageToBibliographyLinks = ({
                                     id,
                                     relatedData,
                                     fields,
                                     tirage,
                                     isAddMode,
                                     columnsLink,
                                     renderRowActions,
                                     searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
                                   }: TirageToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "tirage-links-bibliography-add";
  const STORAGE_KEY_LINKED = "tirage-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resourceId: id,
      resultType: "bibliography",
      searchQuery: searchContent,
      pagination: getPagination,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("bibliographieDescriptionTitre"),
        size: 100,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
          const data = row.original as Bibliography;
          if (!tirage) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={tirage.id}
                  type="tirage"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    tirage: tirage,
                    tirageDansBibliographieDescriptionReproduitPage: "",
                    tirageDansBibliographieDescriptionReproduitPlanche: "",
                    tirageDansBibliographieDescriptionPlancheNonNumerotee: false,
                    tirageDansBibliographieDescriptionCitePage: "",
                    tirageDansBibliographieDescriptionListePage: "",
                    tirageDansBibliographieDescriptionSousNumero: "",
                    tirageDansBibliographieDescriptionPhotographe: "",
                    tirageDansBibliographieDescriptionReproductionCouleur: false,
                    tirageDansBibliographieDescriptionReproductionCouverture: false,
                    tirageDansBibliographieDescriptionNonPagine: false,
                    tirageDansBibliographieDescriptionTexteLibre: "",
                    tirageDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [tirage, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsBibliographies} data={notRelatedBibliographies?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedBibliographies?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <TirageInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="tirage"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const BibliographyToTirageLinks = ({
                                     id,
                                     relatedData,
                                     fields,
                                     bibliography,
                                     isAddMode,
                                     columnsLink,
                                     renderRowActions,
                                     searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
                                   }: BibliographyToTirageLinksProps) => {
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_T = "bibliography-links-tirage-add";
  const STORAGE_KEY_LINKED_T = "bibliography-links-tirage-linked";
  const defaultVisibilityT = { ...tiragesColumnsVisibility, actions: true };
  const [columnVisibilityAddT, setColumnVisibilityAddT] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_T);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityT, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityT };
    });
  const [columnVisibilityLinkedT, setColumnVisibilityLinkedT] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_T);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityT, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityT };
    });
  const [columnsOrderAddT, setColumnsOrderAddT] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_T);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedT, setColumnsOrderLinkedT] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_T);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resourceId: id,
    resultType: "tirage",
    searchQuery: searchContent,
    pagination: getPagination,
  });

  const { t } = useTranslation();

  const columnsTirages: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "tirageDescriptionNumeroEnregistrement",
        header: t("tirageDescriptionNumeroEnregistrement"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.tirageDescriptionNumeroEnregistrement}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "tirageDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Tirage> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="tirage"
          />
        ),
      },
      createActionsColumnDef<Tirage>(({ row }: { row: Row<Tirage> }) => {
          const data = row.original as Tirage;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="tirage"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("tirageDescriptionNumeroEnregistrement"),
                  value: data.tirageDescriptionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("tirageDescriptionNotes"),
                  value: data.tirageDescriptionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <TirageInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={bibliography.id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: bibliography,
                    tirage: data,
                    tirageDansBibliographieDescriptionReproduitPage: "",
                    tirageDansBibliographieDescriptionReproduitPlanche: "",
                    tirageDansBibliographieDescriptionPlancheNonNumerotee: false,
                    tirageDansBibliographieDescriptionCitePage: "",
                    tirageDansBibliographieDescriptionListePage: "",
                    tirageDansBibliographieDescriptionSousNumero: "",
                    tirageDansBibliographieDescriptionPhotographe: "",
                    tirageDansBibliographieDescriptionReproductionCouleur: false,
                    tirageDansBibliographieDescriptionReproductionCouverture: false,
                    tirageDansBibliographieDescriptionNonPagine: false,
                    tirageDansBibliographieDescriptionTexteLibre: "",
                    tirageDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [bibliography, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsTirages} data={notAlreadyRelatedTirages?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedTirages?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_T} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddT, onColumnOrderChange: setColumnsOrderAddT, state: { columnVisibility: columnVisibilityAddT, columnOrder: columnsOrderAddT } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_T} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedT, onColumnOrderChange: setColumnsOrderLinkedT, state: { columnVisibility: columnVisibilityLinkedT, columnOrder: columnsOrderLinkedT } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_T}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <TirageLinkGridCard
                  tirage={row.tirage}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <TirageInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default TirageInBibliographyLinks;
